import { useAcceptOAuthCredentials, useTwoFATokenLogin } from "@/api/mutations/auth";
import { OtpConfirmationDialog } from "@/components/otp-confirmation-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ACCESS_TOKEN } from "@/constants/auth";
import { Loader2, ShieldCheck } from "lucide-react";
import { useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export const AuthGoogleCallback = () => {
  const { mutateAsync } = useAcceptOAuthCredentials();
  const { mutateAsync: loginUsingToken, isPending } = useTwoFATokenLogin();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const refreshToken = searchParams.get("refreshToken");
  const accessToken = searchParams.get("accessToken");
  const twoFAMode = searchParams.get("type") === "twoFa";

  useEffect(() => {
    if (!refreshToken || !accessToken) return;

    localStorage.setItem(ACCESS_TOKEN, accessToken);

    mutateAsync({ refreshToken })
      .then(() => navigate("/dashboard"))
      .catch(() => navigate("/login"));
  }, []);

  if (twoFAMode) {
    return (
      <OtpConfirmationDialog
        open
        isLoading={isPending}
        onSubmit={(otp) => {
          loginUsingToken({
            token: searchParams.get("token") ?? "",
            otp,
          })
            .then((res) => {
              localStorage.setItem(ACCESS_TOKEN, res.data.accessToken);

              navigate("/dashboard");
            })
            .catch(() => {
              toast.error("Wrong OTP provided");
            });
        }}
      />
    );
  }

  if (!refreshToken || !accessToken) {
    return <Navigate to={`/login?${searchParams.toString()}`} />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="w-full items-center text-center">
          <div className="w-full flex justify-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <ShieldCheck className="h-7 w-7 text-primary" />
            </div>
          </div>

          <CardTitle>Signing you in...</CardTitle>

          <CardDescription>
            Please wait while we securely verify your Google account.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />

            <p className="text-center text-sm text-muted-foreground">
              This should only take a few seconds.
            </p>
          </div>

          <Alert>
            <AlertTitle>Secure Authentication</AlertTitle>

            <AlertDescription>
              Your identity is being verified and your session is being established. You will be
              redirected automatically.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};
